import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { useAgentDialog } from '@/modules/use-agent-dialog';
import { track } from '@/shared/utils/track';

type Dialog = ReturnType<typeof useAgentDialog>;

// The composer both AI panels share: the chat/history view, the draft, the
// edit in progress with its confirmation step, stick-to-bottom scrolling
// with the fade under the thread, and Escape peeling one layer at a time
// (the confirmation, then the edit, then the panel). The panel owns what
// differs: the analytics event and the context a question ships with
// (`send`), and where the composer's focus goes.
export function useAgentComposer<Source extends string>({
  open,
  onClose,
  dialog,
  surface,
  send,
  focusComposer,
}: {
  open: boolean;
  onClose: () => void;
  dialog: Pick<Dialog, 'messages' | 'busy' | 'failure' | 'edit' | 'openDialog'>;
  surface: 'widget' | 'market';
  send: (question: string, source: 'input' | 'retry' | Source) => void;
  focusComposer: () => void;
}) {
  const { messages, busy, failure, edit, openDialog } = dialog;
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<{
    messageId: string;
    confirm: boolean;
  } | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef(0);
  // A user who scrolled up to read must not be yanked back to the bottom
  // while the answer streams.
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editing === null) onClose();
      else if (editing.confirm) setEditing({ ...editing, confirm: false });
      else {
        setEditing(null);
        setQuery('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, editing]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop =
      stickToBottomRef.current ? el.scrollHeight : scrollTopRef.current;
    setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  }, [messages, busy, failure, view]);

  const askQuestion = (raw: string, source: 'input' | 'retry' | Source) => {
    const question = raw.trim();
    if (question === '' || busy !== null) return;
    send(question, source);
    setEditing(null);
    setQuery('');
    stickToBottomRef.current = true;
  };

  const submitComposer = () => {
    if (editing === null) {
      askQuestion(query, 'input');
      return;
    }
    if (query.trim() === '' || busy !== null) return;
    setEditing({ ...editing, confirm: true });
  };

  const startEditing = (message: { id: string; text: string }) => {
    setEditing({ messageId: message.id, confirm: false });
    setQuery(message.text);
    focusComposer();
  };

  const cancelEditing = () => {
    setEditing(null);
    setQuery('');
  };

  const keepEditing = () => {
    if (editing !== null) setEditing({ ...editing, confirm: false });
  };

  const confirmEdit = () => {
    if (editing === null) return;
    track('ai_message_edited', { surface });
    edit(editing.messageId, query);
    setEditing(null);
    setQuery('');
    stickToBottomRef.current = true;
  };

  const openHistory = () => {
    track('ai_history_opened', { surface });
    setView('history');
  };

  const showChat = () => setView('chat');

  const openHistoryDialog = (id: string) => {
    cancelEditing();
    openDialog(id);
    setView('chat');
    stickToBottomRef.current = true;
  };

  const onThreadScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    scrollTopRef.current = el.scrollTop;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = gap < 40;
    setCanScrollDown(gap > 1);
  };

  return {
    view,
    query,
    setQuery,
    editing,
    canScrollDown,
    scrollRef,
    askQuestion,
    submitComposer,
    startEditing,
    cancelEditing,
    keepEditing,
    confirmEdit,
    openHistory,
    showChat,
    openHistoryDialog,
    onThreadScroll,
  };
}
